import { useCallback } from 'react'
import { useChatStore } from '@/store/chat/chatStore'
import type { ContextLengthPart } from '@/store/chat/chatStore'
import { chatService } from '@/lib/api/chat'
import { toast } from 'sonner'
import { MessageSquare } from 'lucide-react'
import { resolveAssistantContent } from './assistantContent'

// Simple UUID generator
function generateUUID() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0
    const v = c === 'x' ? r : (r & 0x3) | 0x8
    return v.toString(16)
  })
}

export function useChat() {
  const {
    conversations,
    currentConversationId,
    messages,
    streamingContent,
    isStreaming,
    isLoadingConversations,
    isLoadingMessages,
    error,
    failedMessageId,
    pendingEnabledTools,
    setConversations,
    setCurrentConversationId,
    setMessages,
    addMessage,
    removeMessage,
    addStreamingContent,
    clearStreamingContent,
    setIsStreaming,
    setIsLoadingConversations,
    setIsLoadingMessages,
    setError,
    setFailedMessageId,
    setPendingEnabledTools,
  } = useChatStore()

  // Load all conversations
  const loadConversations = useCallback(async () => {
    try {
      setIsLoadingConversations(true)
      setError(null)
      const data = await chatService.getConversations()
      setConversations(data)
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to load conversations'
      setError(message)
      toast.error(message)
    } finally {
      setIsLoadingConversations(false)
    }
  }, [setConversations, setError, setIsLoadingConversations])

  // Load messages for a conversation
  const loadMessages = useCallback(
    async (conversationId: string) => {
      try {
        setIsLoadingMessages(true)
        setError(null)
        console.log('[useChat] Loading messages for conversation:', conversationId)
        const data = await chatService.getConversation(conversationId)
        console.log('[useChat] Loaded messages:', data.messages.length, 'messages')
        console.log('[useChat] Messages:', data.messages)
        setMessages(data.messages)
        setCurrentConversationId(conversationId)
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to load messages'
        console.error('[useChat] Error loading messages:', err)
        setError(message)
        toast.error(message)
      } finally {
        setIsLoadingMessages(false)
      }
    },
    [setMessages, setCurrentConversationId, setError, setIsLoadingMessages],
  )

  // Create new conversation
  const createConversation = useCallback(
    async (title: string, clearMessages = true) => {
      try {
        const conversation = await chatService.createConversation(title)
        setConversations([conversation, ...conversations])
        setCurrentConversationId(conversation.uuid)
        if (clearMessages) {
          setMessages([])
        }
        toast.success('New conversation created', {
          description: 'You can now start testing MCP tools.',
          icon: <MessageSquare className="w-4 h-4" />,
        })
        return conversation.uuid
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to create conversation'
        toast.error(message)
        throw err
      }
    },
    [conversations, setConversations, setCurrentConversationId, setMessages],
  )

  // Show an empty chat view WITHOUT creating a conversation. The conversation is
  // created only when the user sends the first message (conversation-on-first-message
  // rule); the tool selection resets to the user's default for the new chat.
  const startNewConversation = useCallback(() => {
    setCurrentConversationId(null)
    setMessages([])
    setError(null)
    setFailedMessageId(null)
    setPendingEnabledTools(null)
  }, [setCurrentConversationId, setMessages, setError, setFailedMessageId, setPendingEnabledTools])

  // Send message with streaming
  const sendMessage = useCallback(
    async (messageText: string, conversationIdOverride?: string | null, model?: string | null) => {
      const userMessageId = generateUUID()
      try {
        setError(null)
        setFailedMessageId(null)

        // Use override if provided, otherwise use current conversation ID
        const conversationId =
          conversationIdOverride !== undefined ? conversationIdOverride : currentConversationId
        const userMessage = {
          uuid: userMessageId,
          conversation_uuid: conversationId || '',
          role: 'user' as const,
          content: messageText,
          created_at: new Date().toISOString(),
        }
        addMessage(userMessage)

        // Start streaming
        setIsStreaming(true)
        clearStreamingContent()

        // Track accumulated text and streaming content separately
        let accumulatedText = ''
        let previousTextLength = 0 // Track where we were in the text
        const capturedStreamingContent: typeof streamingContent = []

        await chatService.sendMessage(
          messageText,
          conversationId,
          // onChunk - handle both text and tool call events
          (content) => {
            if (content.type === 'text') {
              // Update accumulated text
              accumulatedText = content.content || ''
            }
            // Capture streaming content locally WITH text delta
            if (content.type === 'text') {
              // For text, capture the new portion (delta)
              const currentLength = (content.content || '').length
              if (currentLength > previousTextLength) {
                const delta = (content.content || '').substring(previousTextLength)
                if (delta) {
                  capturedStreamingContent.push({
                    type: 'text',
                    content: delta, // Just the new text
                  })
                }
                previousTextLength = currentLength
              }
            } else {
              // For tool calls and max_iterations, capture as-is
              capturedStreamingContent.push(content)
            }
            // Add content (store handles text replacement and tool call updates)
            addStreamingContent(content)
          },
          // onComplete
          async (returnedConversationUuid, generationId) => {
            console.log('[useChat] Stream complete. Returned UUID:', returnedConversationUuid)
            console.log('[useChat] Generation ID:', generationId)
            console.log('[useChat] Used conversation ID:', conversationId)
            console.log('[useChat] Final accumulated text length:', accumulatedText.length)
            console.log('[useChat] Captured streaming content:', capturedStreamingContent)

            // Use the conversation ID that was passed to sendMessage, or the returned one
            const finalConversationId = returnedConversationUuid || conversationId

            // If backend created a new conversation, update our state immediately
            if (returnedConversationUuid) {
              console.log(
                '[useChat] Backend created/returned conversation:',
                returnedConversationUuid,
              )
              setCurrentConversationId(returnedConversationUuid)
              // Clear the pre-first-message tool selection — it has been consumed
              setPendingEnabledTools(null)
            }

            // Convert captured streaming content to message parts
            // Build parts in exact chronological order
            const parts: import('@/store/chat/chatStore').MessagePart[] = []
            const seenToolCalls = new Map<string, number>() // Track tool call positions

            capturedStreamingContent.forEach((item) => {
              if (item.type === 'text' && item.content) {
                // Add text segment
                parts.push({
                  type: 'text',
                  text: item.content,
                })
              } else if (item.type === 'tool_call' && item.toolCall) {
                const toolId = item.toolCall.tool_call_id

                if (!seenToolCalls.has(toolId)) {
                  // New tool call - add it
                  seenToolCalls.set(toolId, parts.length)
                  parts.push({
                    type: 'tool-invocation',
                    toolInvocation: {
                      toolCallId: item.toolCall.tool_call_id,
                      toolName: item.toolCall.tool_name,
                      state: item.toolCall.success !== undefined ? 'result' : 'call',
                      args: item.toolCall.arguments
                        ? (() => {
                            try {
                              return JSON.parse(item.toolCall.arguments!)
                            } catch {
                              return item.toolCall.arguments
                            }
                          })()
                        : undefined,
                      result: item.toolCall.result
                        ? (() => {
                            try {
                              return JSON.parse(item.toolCall.result!)
                            } catch {
                              return item.toolCall.result
                            }
                          })()
                        : undefined,
                    },
                  })
                } else {
                  // Update existing tool call
                  const toolIndex = seenToolCalls.get(toolId)!
                  const part = parts[
                    toolIndex
                  ] as import('@/store/chat/chatStore').ToolInvocationPart
                  part.toolInvocation.state =
                    item.toolCall.success !== undefined ? 'result' : 'call'
                  if (item.toolCall.result) {
                    part.toolInvocation.result = (() => {
                      try {
                        return JSON.parse(item.toolCall.result!)
                      } catch {
                        return item.toolCall.result
                      }
                    })()
                  }
                }
              } else if (item.type === 'max_iterations' && item.maxIterations) {
                // Add max iterations warning part
                parts.push({
                  type: 'max-iterations',
                  maxIterations: {
                    message: item.maxIterations.message,
                    iterations: item.maxIterations.iterations,
                    suggestion: item.maxIterations.suggestion,
                  },
                })
              } else if (item.type === 'context_length' && item.contextLength) {
                // Add context length exceeded part
                parts.push({
                  type: 'context-length',
                  contextLength: {
                    message: item.contextLength.message,
                    suggestion: item.contextLength.suggestion,
                  },
                })
              } else if (item.type === 'output_truncated' && item.outputTruncated) {
                // Add output truncated warning part
                parts.push({
                  type: 'output-truncated',
                  outputTruncated: {
                    message: item.outputTruncated.message,
                    suggestion: item.outputTruncated.suggestion,
                  },
                })
              }
            })

            // Merge consecutive text parts to avoid fragmentation
            const mergedParts: import('@/store/chat/chatStore').MessagePart[] = []
            for (const part of parts) {
              if (part.type === 'text') {
                const lastPart = mergedParts[mergedParts.length - 1]
                if (lastPart && lastPart.type === 'text') {
                  // Merge with previous text part
                  lastPart.text += part.text
                } else {
                  // Add new text part
                  mergedParts.push(part)
                }
              } else {
                // Add tool-invocation as-is
                mergedParts.push(part)
              }
            }

            // Resolve the content to persist so a turn is NEVER silently dropped.
            // A reasoning model can finish (finish_reason "stop") with only
            // "thinking" tokens — no visible text and no tool calls. Previously the
            // guard below required text or parts, so such a turn was dropped and the
            // streamed bubble cleared, leaving the user's message dangling.
            // resolveAssistantContent supplies a recoverable note for that case.
            const hasToolParts = mergedParts.some((p) => p.type === 'tool-invocation')
            const finalContent = resolveAssistantContent(accumulatedText, hasToolParts)
            if (finalConversationId) {
              const assistantMessage = {
                uuid: generateUUID(),
                conversation_uuid: finalConversationId,
                role: 'assistant' as const,
                content: finalContent, // Legacy field for backend
                parts: mergedParts.length > 0 ? mergedParts : undefined, // Structured content
                created_at: new Date().toISOString(),
              }
              addMessage(assistantMessage)
              console.log('[useChat] Added assistant message with parts:', mergedParts.length)
              console.log('[useChat] Parts breakdown:', mergedParts.map((p) => p.type).join(' → '))

              // Save to backend with parts and generation_id
              try {
                await chatService.saveMessage(finalConversationId, {
                  role: 'assistant',
                  content: finalContent,
                  generation_id: generationId,
                  parts:
                    mergedParts.length > 0
                      ? mergedParts.map((part) => {
                          if (part.type === 'text') {
                            return { type: 'text', text: part.text }
                          } else if (part.type === 'tool-invocation') {
                            return {
                              type: 'tool-invocation',
                              toolInvocation: part.toolInvocation,
                            }
                          } else if (part.type === 'max-iterations') {
                            return {
                              type: 'max-iterations',
                              maxIterations: part.maxIterations,
                            }
                          } else if (part.type === 'output-truncated') {
                            return {
                              type: 'output-truncated',
                              outputTruncated: part.outputTruncated,
                            }
                          } else {
                            // context-length type
                            return {
                              type: 'context-length',
                              contextLength: (part as ContextLengthPart).contextLength,
                            }
                          }
                        })
                      : undefined,
                })
                console.log('[useChat] Saved message with parts to backend')
              } catch (error) {
                console.error('[useChat] Failed to save message to backend:', error)
                // Don't fail the UX if backend save fails - message is already in memory
              }
            }

            // Clear streaming state
            setIsStreaming(false)
            clearStreamingContent()

            // Reload conversation list to update message counts and timestamps
            loadConversations()
          },
          // onError
          (error) => {
            setIsStreaming(false)
            clearStreamingContent()
            setError(error.message)
            setFailedMessageId(userMessageId)
          },
          model,
          conversationId ? null : pendingEnabledTools,
        )
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to send message'
        setError(message)
        setFailedMessageId(userMessageId)
        setIsStreaming(false)
        clearStreamingContent()
      }
    },
    [
      currentConversationId,
      pendingEnabledTools,
      addMessage,
      setIsStreaming,
      clearStreamingContent,
      addStreamingContent,
      setError,
      setFailedMessageId,
      setCurrentConversationId,
      setPendingEnabledTools,
      loadConversations,
    ],
  )

  // Retry a failed message
  const retryMessage = useCallback(
    async (messageId: string, model?: string | null) => {
      const failedMsg = messages.find((m) => m.uuid === messageId)
      if (!failedMsg) return

      // Remove the failed user message and clear error state
      removeMessage(messageId)
      setError(null)
      setFailedMessageId(null)

      // Resend with the same model that was active when the message failed
      await sendMessage(failedMsg.content, undefined, model)
    },
    [messages, removeMessage, setError, setFailedMessageId, sendMessage],
  )

  // Delete conversation
  const deleteConversation = useCallback(
    async (conversationId: string) => {
      try {
        await chatService.deleteConversation(conversationId)
        setConversations(conversations.filter((c) => c.uuid !== conversationId))

        if (currentConversationId === conversationId) {
          setCurrentConversationId(null)
          setMessages([])
        }

        toast.success('Conversation deleted')
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to delete conversation'
        toast.error(message)
      }
    },
    [conversations, currentConversationId, setConversations, setCurrentConversationId, setMessages],
  )

  // Update conversation title
  const updateConversationTitle = useCallback(
    async (conversationId: string, title: string) => {
      try {
        await chatService.updateConversation(conversationId, title)
        setConversations(
          conversations.map((c) => (c.uuid === conversationId ? { ...c, title } : c)),
        )
        toast.success('Title updated')
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Failed to update title'
        toast.error(message)
      }
    },
    [conversations, setConversations],
  )

  return {
    // State
    conversations,
    currentConversationId,
    messages,
    streamingContent,
    isStreaming,
    isLoadingConversations,
    isLoadingMessages,
    error,
    failedMessageId,

    // Actions
    loadConversations,
    loadMessages,
    createConversation,
    startNewConversation,
    sendMessage,
    retryMessage,
    deleteConversation,
    updateConversationTitle,
  }
}
